// PosterUploader.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, CheckCircle, UploadCloud, AlertTriangle } from "lucide-react";

export default function PosterUploader() {
    const [form, setForm] = useState({ file: null, customName: "", preview: "" });
    const [result, setResult] = useState({ keywords: [], selectedKeywords: [], filePath: "" });
    const [registeredKeywords, setRegisteredKeywords] = useState([]);
    const [loading, setLoading] = useState(false); // 로딩 상태
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [manualKeyword, setManualKeyword] = useState("");
    const fileInputRef = useRef();

    useEffect(() => {
        fetchRegisteredKeywords();
    }, []);

    const fetchRegisteredKeywords = () => {
        axios
            .get(`${process.env.REACT_APP_API_BASE_URL}/poster/registered-keywords`)
            .then((res) => setRegisteredKeywords([...new Set(res.data)]))
            .catch(console.error);
    };

    const resetAll = () => {
        setForm({ file: null, customName: "", preview: "" });
        setResult({ keywords: [], selectedKeywords: [], filePath: "" });
        setManualKeyword("");
    };

    const recommendSimilarKeywords = (gptKeywords, allRegistered) =>
        allRegistered.filter((existing) =>
            gptKeywords.some((gpt) => existing.includes(gpt))
        );

    const handleFileChange = async (e) => {
        const selected = e.target.files[0];
        if (!selected) return;

        const previewUrl = URL.createObjectURL(selected);
        const customName = selected.name.replace(/\.[^.]+$/, "");

        setForm({ file: selected, preview: previewUrl, customName });
        setResult({ keywords: [], selectedKeywords: [], filePath: "" });

        const formData = new FormData();
        formData.append("file", selected);
        formData.append("name", customName);

        try {
            setLoading(true); // 업로드(추출) 시작
            const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/poster/upload`,
                formData
            );
            setLoading(false); // 업로드(추출) 완료

            let keywords = [];
            if (Array.isArray(res.data.keywords)) {
                keywords = res.data.keywords.flatMap((k) =>
                    typeof k === "string" ? k.split(/\s+/) : k
                );
            } else if (typeof res.data.keywords === "string") {
                keywords = res.data.keywords.split(/\s+/);
            }

            const uniqueSortedKeywords = [...new Set(keywords)].sort((a, b) =>
                a.localeCompare(b, "ko")
            );
            const recommended = recommendSimilarKeywords(
                uniqueSortedKeywords,
                registeredKeywords
            );

            setResult({
                keywords: uniqueSortedKeywords,
                filePath: res.data.filePath,
                selectedKeywords: recommended,
            });
        } catch (e) {
            setLoading(false);
            console.error(e);
            alert("키워드 추출 실패");
        }
    };

    const toggleKeyword = (k) => {
        setResult((prev) => ({
            ...prev,
            selectedKeywords: prev.selectedKeywords.includes(k)
                ? prev.selectedKeywords.filter((x) => x !== k)
                : [...prev.selectedKeywords, k],
        }));
    };

    const handleConfirm = async () => {
        if (!result.filePath) return alert("파일 경로가 없습니다.");
        if (!result.selectedKeywords.length) return alert("키워드를 선택해주세요.");

        const payload = {
            name: form.customName,
            filePath: result.filePath,
            keywords: result.selectedKeywords,
        };

        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/poster/confirm`,
                payload
            );
            alert("저장 완료");
            resetAll();
            fetchRegisteredKeywords();
        } catch (e) {
            console.error(e);
            alert("저장 실패");
        }
    };

    const handleManualKeywordKeyDown = (e) => {
        if (e.key === "Enter" && manualKeyword.trim()) {

            if ( manualKeyword.length <= 1 ) {
                setManualKeyword("")
                return
            }

            e.preventDefault();
            const keyword = manualKeyword.trim();
            keyword.replace(",", "");
            if (!result.keywords.includes(keyword)) {
                setResult((prev) => ({
                    ...prev,
                    keywords: [...prev.keywords, keyword],
                    selectedKeywords: [...prev.selectedKeywords, keyword],
                }));
            } else if (!result.selectedKeywords.includes(keyword)) {
                toggleKeyword(keyword);
            }
            setManualKeyword("");
        }
    };

    const sortedRegistered = [...new Set(registeredKeywords)].sort((a, b) => {
        const isA = result.keywords.some((k) => a.includes(k));
        const isB = result.keywords.some((k) => b.includes(k));
        if (isA && !isB) return -1;
        if (!isA && isB) return 1;
        return a.localeCompare(b, "ko");
    });

    const handleFileSelectClick = () => {
        if (form.file) {
            setShowConfirmModal(true);
        } else {
            fileInputRef.current?.click();
        }
    };

    const confirmFileReplace = () => {
        setShowConfirmModal(false);
        fileInputRef.current?.click();
    };

    return (
        <div className="slow-animation max-w-4xl mx-auto p-6 space-y-6 text-gray-800">
            {/* 선택된 키워드 표시 */}
            {result.selectedKeywords.length > 0 && (
                <div className="bg-white shadow rounded-xl border p-4">
                    <h3 className="font-bold text-gray-700 mb-2">선택된 키워드</h3>
                    <div className="flex flex-wrap gap-2">
                        {result.selectedKeywords.map((k, i) => (
                            <span
                                key={i}
                                className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm"
                            >
                                {k}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* 포스터 카드 */}
            <div className="bg-white rounded-xl shadow-md p-6 border">
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 space-y-2">
                        <p className="text-sm text-gray-600">
                            포스터 이름:{" "}
                            <span className="font-medium">{form.customName}</span>
                        </p>

                        {result.keywords.length > 0 && (
                            <>
                                <h4 className="font-semibold mt-4">추출된 키워드</h4>
                                <div className="flex flex-wrap gap-2">
                                    {result.keywords.map((k, i) => (
                                        <span
                                            key={i}
                                            onClick={() => toggleKeyword(k)}
                                            className={`cursor-pointer px-3 py-1 rounded-full text-sm border transition ${
                                                result.selectedKeywords.includes(k)
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-gray-100 text-gray-800 hover:bg-blue-100"
                                            }`}
                                        >
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* 수동 키워드 입력 */}
                        <input
                            type="text"
                            value={manualKeyword}
                            onChange={(e) => setManualKeyword(e.target.value)}
                            onKeyDown={handleManualKeywordKeyDown}
                            placeholder="추가할 키워드 입력 후 Enter"
                            className="mt-4 w-full border px-3 py-2 rounded text-sm focus:outline-none focus:ring focus:border-blue-400"
                        />
                    </div>

                    <div className="w-48">
                        {form.preview && (
                            <img
                                src={form.preview}
                                alt="preview"
                                className="border rounded shadow-sm w-full object-contain"
                            />
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <button
                        onClick={handleFileSelectClick}
                        disabled={loading}
                        className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                    >
                        <UploadCloud className="w-4 h-4" />
                        {form.file ? "다른 파일 선택" : "파일 선택하기"}
                    </button>
                    <input
                        id="file-input"
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        disabled={loading}
                        className="hidden"
                    />
                </div>
            </div>

            {/* 등록된 키워드 */}
            {sortedRegistered.length > 0 && (
                <div className="bg-white rounded-xl shadow p-6 border">
                    <h3 className="font-semibold mb-3">등록된 키워드</h3>
                    <p className="text-sm text-gray-400">
                        ⭐ 모양은 추출 키워드와 유사한 항목입니다.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                        {sortedRegistered.map((k, i) => {
                            const isSelected = result.selectedKeywords.includes(k);
                            const isRecommended = result.keywords.some((gpt) => k.includes(gpt));
                            return (
                                <span
                                    key={i}
                                    onClick={() => toggleKeyword(k)}
                                    className={`cursor-pointer px-3 py-1 rounded-full text-sm border transition ${
                                        isSelected
                                            ? "bg-blue-600 text-white"
                                            : isRecommended
                                                ? "bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200"
                                                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                    }`}
                                >
                                    {isRecommended && "⭐ "}
                                    {k}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 확정 버튼 */}
            {result.keywords.length > 0 && (
                <div className="text-right">
                    <button
                        onClick={handleConfirm}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded shadow inline-flex items-center gap-2"
                    >
                        <CheckCircle className="w-4 h-4" />
                        확정 저장
                    </button>
                </div>
            )}

            {/* 로딩 오버레이 */}
            {loading && (
                <div className="fixed inset-0 bg-gray-100 bg-opacity-40 flex items-start justify-center pt-[30vh] z-50">
                    <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
                        <Loader2 className="animate-spin w-5 h-5 text-blue-600" />
                        키워드 추출 중입니다...
                    </div>
                </div>
            )}

            {/* 파일 교체 확인 모달 */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-opacity-40 flex items-start justify-center pt-[30vh] z-50">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm text-center">
                        <AlertTriangle className="mx-auto text-yellow-500 mb-4 w-8 h-8" />
                        <p className="font-semibold mb-2">파일을 다시 선택하시겠습니까?</p>
                        <p className="text-sm text-gray-500 mb-4">
                            기존 포스터 정보와 키워드가 초기화됩니다.
                        </p>
                        <div className="flex justify-center gap-4">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                            >
                                취소
                            </button>
                            <button
                                onClick={confirmFileReplace}
                                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}